/* oxlint-disable no-console */
import { tradeLogger } from '@flipster/utils/datadog';
import { startPingPongLoop, type PingPongLoop } from '@flipster/utils/ping-pong';

import { getMessageTransport } from './transport';
import type { MessageTransport } from './transport/type';
import type { AppMessage } from './wire/app-message';
import type { WebViewMessage } from './wire/webview-message';

export type BridgeState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED';

export interface WebViewBridgeProtocolBase<OutgoingMetadata, IncomingMetadata, IncomingMessage> {
  channelName: string;
  metadata: OutgoingMetadata;
  onMetadata: (metadata: IncomingMetadata) => void;
  onMessage: (message: IncomingMessage) => void;
}

export interface WebViewBridgeProtocol<AppMsg, WebMsg, AppMeta, WebMeta> extends WebViewBridgeProtocolBase<
  WebMeta,
  AppMeta,
  AppMsg
> {
  onPause?: () => void;
  onResume?: () => void;
  onConnectionChanged: (state: BridgeState) => void;
  onConnectionError?: (error: Error) => void;
  // This is injected by bridge and should be left null as is.
  sendMessage: ((message: WebMsg) => void) | null;
}

export default class WebViewBridge<AppMsg, WebMsg, AppMeta, WebMeta> {
  #transport: MessageTransport<AppMessage<AppMsg>, WebViewMessage<WebMsg>> | null = null;
  #protocol: WebViewBridgeProtocol<AppMsg, WebMsg, AppMeta, WebMeta>;
  #abortController: AbortController | undefined;
  #pingPongLoopController: AbortController | undefined;
  #pingPongLoop: PingPongLoop | undefined;
  #webSeq = NaN;
  #appSeq = NaN;
  #usePauseResume = false;
  #isPaused = false;
  #pauseSeq = NaN;

  currentState: BridgeState = 'DISCONNECTED';

  constructor(protocol: WebViewBridgeProtocol<AppMsg, WebMsg, AppMeta, WebMeta>) {
    this.#protocol = protocol;
    if (typeof window !== 'undefined') {
      window.prexBridge.bridges[protocol.channelName] = this;
    }
  }

  toString() {
    return `WebViewBridge(${this.#protocol.channelName})`;
  }

  async start() {
    tradeLogger.info('webview_bridge_start');
    if (this.#isPaused) return;

    this.stop();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.setState('CONNECTING');

    for (;;) {
      if (abortController.signal.aborted) return;
      try {
        this.#transport = await getMessageTransport<AppMeta, WebMeta, AppMessage<AppMsg>, WebViewMessage<WebMsg>>(
          this.#protocol.channelName,
          { messageSeq: true, pauseResume: true, ...this.#protocol.metadata },
          (appMetadata) => {
            this.#protocol.onMetadata(appMetadata);
            if ((appMetadata as { pauseResume: boolean }).pauseResume) this.#usePauseResume = true;
          },
          this.onMessage.bind(this),
          abortController.signal,
        );
        break;
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (err instanceof Error) this.#protocol.onConnectionError?.(err);
      }
    }

    this.#webSeq = 0;
    this.#pauseSeq = 0;
    this.#protocol.sendMessage = this.sendMessage.bind(this);
    this.sendWebViewMessage({ type: 'ack', sessionId: this.#transport.sessionId });
    this.setState('CONNECTED');
    const pingPongLoopController = new AbortController();
    this.startPingPongLoop(pingPongLoopController.signal);
    this.#pingPongLoopController = pingPongLoopController;
  }

  stop() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#pingPongLoopController?.abort();
    this.#pingPongLoopController = undefined;
    this.#webSeq = NaN;
    this.#appSeq = NaN;
    this.#protocol.sendMessage = null;
    this.#transport?.close();
    this.#transport = null;
    this.#usePauseResume = false;
    this.#isPaused = false;
    this.setState('DISCONNECTED');
  }

  private pause() {
    this.#pauseSeq += 1;
    this.#appSeq = NaN;
    this.#webSeq = 0;
    if (!this.#isPaused) {
      this.#isPaused = true;
      this.#protocol.onPause?.();
      this.setState('DISCONNECTED');
      this.#pingPongLoopController?.abort();
      this.#pingPongLoopController = undefined;
    }
  }

  private resume() {
    if (this.#isPaused) {
      this.#isPaused = false;
      this.#protocol.onResume?.();
      this.setState('CONNECTED');
      const pingPongLoopController = new AbortController();
      this.startPingPongLoop(pingPongLoopController.signal);
      this.#pingPongLoopController = pingPongLoopController;
    }
  }

  private sendWebViewMessage(message: WebViewMessage<WebMsg>) {
    if (window.prexBridge.flags.logWireMessages) console.log('[WVBridge - W->A]', JSON.stringify(message));
    this.#transport?.sendMessage(message);
  }

  private onMessage(data: AppMessage<AppMsg>) {
    if (window.prexBridge.flags.logWireMessages) {
      const shouldLog = data.type === 'message' ? this.shouldLogMessage(data.message) : true;
      if (shouldLog) {
        console.log('[WVBridge - A->W]', JSON.stringify(data));
      }
    }
    if (data.type === 'ping') {
      this.sendWebViewMessage({ type: 'pong' });
    } else if (data.type === 'pong') {
      this.#pingPongLoop?.pong();
    } else if (data.type === 'stop') {
      tradeLogger.info('webview_bridge_stop');
      this.stop();
    } else if (data.type === 'pause' && this.#usePauseResume) {
      tradeLogger.info('webview_bridge_pause');
      this.pause();
    } else if (data.type === 'resume' && this.#usePauseResume) {
      tradeLogger.info('webview_bridge_resume');
      this.resume();
    } else if (data.type === 'message') {
      if (window.prexBridge.flags.logMessages) {
        const shouldLog = this.shouldLogMessage(data.message);
        if (shouldLog) {
          console.log('[WVBridge - A->W]', JSON.stringify(data.message));
        }
      }
      try {
        if ('seq' in data) this.checkAppSeq(data.seq);
        this.#protocol.onMessage(data.message);
      } catch {
        this.stop();
      }
    }
  }

  private startPingPongLoop(abortSignal: AbortSignal) {
    this.#pingPongLoop = startPingPongLoop({
      abortSignal,
      timeoutMs: 5000,
      waitMs: 2000,
      ping: () => this.sendWebViewMessage({ type: 'ping' }),
      timeout: () => {
        tradeLogger.warn('webview_bridge_not_responding', {
          error_category: 'network_errors',
          error_message: 'WebView Bridge ping timeout',
        });
        this.stop();
      },
    });
  }

  private checkAppSeq(seq: number) {
    const [expected, got] = [this.#appSeq + 1, seq];
    this.#appSeq = seq;
    if (expected === got) return;
    if (isNaN(expected)) return;
    tradeLogger.warn('webview_bridge_message_seq_not_match', {
      error_category: 'network_errors',
      error_message: `WebView Bridge App seq does not match: got ${got}, ${expected} expected`,
      expected,
      got,
    });
    throw new Error('Invalid sequence number');
  }

  private setState(state: BridgeState) {
    if (this.currentState === state) return;
    this.currentState = state;
    this.#protocol.onConnectionChanged(this.currentState);
  }

  private sendMessage(message: WebMsg) {
    if (window.prexBridge.flags.logMessages) console.log('[WVBridge - W->A]', JSON.stringify(message));
    let pauseSeq;
    if (this.#usePauseResume) {
      pauseSeq = this.#pauseSeq;
    }
    this.sendWebViewMessage({
      type: 'message',
      seq: this.#webSeq++,
      message,
      pauseSeq,
    });
  }

  private shouldLogMessage(message: AppMsg): boolean {
    const filter = window.prexBridge.flags.messageFilter;

    // 커스텀 필터가 있으면 우선 적용
    if (filter.customFilter) {
      return filter.customFilter(message);
    }

    // message가 객체가 아니면 로깅
    if (typeof message !== 'object' || message === null) {
      return true;
    }

    const msg = message as Record<string, unknown>;

    // topic 기반 메시지 처리 (message.t가 있는 경우)
    if (msg.t && typeof msg.t === 'object') {
      const topics = Object.keys(msg.t as Record<string, unknown>);

      // topics 필터
      const hasMatchingTopic = filter.topics.length === 0 || topics.some((topic) => filter.topics.includes(topic));
      // types 필터 (u, i, d 등)
      const hasMatchingType =
        filter.types.length === 0 ||
        topics.some((topic) => {
          const topicData = (msg.t as Record<string, unknown>)[topic];
          if (typeof topicData === 'object' && topicData !== null) {
            const types = Object.keys(topicData as Record<string, unknown>);
            return types.some((type) => filter.types.includes(type));
          }
          return false;
        });
      return hasMatchingTopic && hasMatchingType;
    }

    return true;
  }
}

if (typeof window !== 'undefined') {
  window.prexBridge = {
    bridges: {},
    flags: {
      logMessages: false,
      logWireMessages: false,
      messageFilter: {
        // 특정 토픽만 로깅 (예: ['market/tickers', 'market/orderbook'])
        topics: [] as string[],
        // 메시지 타입 필터 (예: ['u', 'i', 'd'] for update, insert, delete)
        types: [] as string[],
        // 커스텀 필터 함수
        customFilter: null as ((message: unknown) => boolean) | null,
      },
    },
  };
}
